<template>
  <md-card class="transfer-confirm">
    <md-progress-bar md-mode="indeterminate" v-if="isPending"/>
    <div class="md-title">{{ i18n.withdraw_confirm() }}</div>

    <md-card-content>
      <div class="transfer-confirm__inner">
        <detail-row :prop="i18n.lbl_asset()"
                    :value="`${opts.destAsset}`"
        />
        <detail-row :prop="i18n.lbl_amount()"
                    :value="`${i18n.c(opts.amount)} ${opts.destAsset}`"/>


        <template v-if="opts.externalDetails.withdrawalType === 'xpay'">
          <detail-row v-if="opts.externalDetails.displayName"
                      :prop="i18n.withdraw_external({ asset: opts.destAsset })"
                      :value="`${opts.externalDetails.displayName}`"/>
        </template>
        <template v-else>
        <detail-row v-if="opts.externalDetails.address"
                    :prop="i18n.withdraw_external({ asset: opts.destAsset })"
                    :value="`${opts.externalDetails.address}`"/>
        </template>

        <div v-else class="withdrawal-cashbox__row">
          <p>{{ i18n.withdraw_cashbox() }}</p>
        </div>

        <div class="md-size-100">
          <div class="withdrawal-fees__label text_small">{{ i18n.lbl_sender_fees() }}</div>
          <detail-row :prop="i18n.lbl_fixed()" :value="`${i18n.c(opts.fee.fixed)} ${opts.destAsset}`" />
          <detail-row :prop="i18n.lbl_percent()" :value="`${i18n.c(opts.fee.percent)} ${opts.destAsset}`" />
        </div>
        <div v-if="currentAssetLimit()" class="md-size-100">
          <h4><b>{{ i18n.lbl_limits() }}</b></h4>
          <div v-if="currentAssetLimit() && currentAssetLimit().dailyLimit" class="withdrawal-daily-limit">
            <p>{{i18n.lim_withdrawal_daily_msg1({asset: opts.destAsset, limit: currentAssetLimit().dailyLimit})}}</p>
            <div v-if="opts.externalDetails.address && opts.destAsset === 'UAH'">
              <p>{{i18n.lim_withdrawal_daily_msg2({asset: opts.destAsset, limit: 14900})}}</p>
            </div>
            <div v-else>
              <p>{{i18n.lim_withdrawal_daily_msg2({asset: opts.destAsset, limit: currentAssetLimit().dailyLimit})}}</p>
            </div>
            <p>{{i18n.lim_withdrawal_daily_msg3()}}</p>
            <br/>
          </div>
          <detail-row v-if="currentAssetLimit() && currentAssetLimit().weeklyLimit"
                      :prop="i18n.lim_weekly_left()" :value="`${currentAssetLimit().weeklyLeft} ${opts.destAsset}`" />
          <detail-row v-if="currentAssetLimit() && currentAssetLimit().monthlyLimit"
                      :prop="i18n.lim_monthly_left()" :value="`${currentAssetLimit().monthlyLeft} ${opts.destAsset}`" />
          <detail-row v-if="currentAssetLimit() && currentAssetLimit().annualLimit"
                      :prop="i18n.lim_annual_left()" :value="`${currentAssetLimit().annualLeft} ${opts.destAsset}`" />

          <div v-if="limitList" class="withdraw__fees-container" :class="{ loading: isFeesLoadPending }">
            <template v-for="(item, key) in limitList" >
              <detail-row
                v-if="item.period === 'day'"
                :prop="i18n.lim_daily_left()"
                :value="(roundCurrency(item.amount_limit - item.amount, opts.tokenCode))" />
              <div v-if="item.period === 'operation' || item.period === 'day'" :key="key">
                <p v-if="item.period === 'operation'">{{ i18n.lim_withdrawal_daily_msg1({ limit: item.amount_limit, asset: opts.destAsset }) }}</p>
                <p v-if="item.period === 'day'">{{ i18n.lim_withdrawal_daily_msg2({ limit: item.amount_limit, asset: opts.destAsset }) }}</p>
                <p>{{ i18n.lim_withdrawal_daily_msg3() }}</p>
              </div>
            </template>
          </div>

        </div>
      </div>
    </md-card-content>
    <div class="btn-wrap space-between">
      <md-button class="btn md-primary" @click="$emit(commonEvents.cancelClickEvent)" :disabled="isPending">
        {{ i18n.lbl_cancel() }}
      </md-button>
      <md-button class="btn md-primary md-yellow" @click="$emit(commonEvents.confirmClickEvent, form.sourcePaysForDest)" :disabled="isPending">
        {{ i18n.lbl_confirm() }}
      </md-button>
    </div>
  </md-card>
</template>

<script>
  import DetailRow from '@vue/app/common/Detail.Row'
  import TextareaField from '@com/fields/TextareaField'

  import { mapGetters, mapActions } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import { commonEvents } from '@js/events/common_events'
  import { formatDate } from '@utils/dates.util'
  import { assetMap } from '@const'
  import { i18n } from '@js/i18n'
  import { add } from '@utils/math.util'
  import CurrencyRound from '@com/mixins/currency.round.mixin'

  export default {
    name: 'withdraw-confirm',
    mixins: [CurrencyRound],
    components: { DetailRow, TextareaField },
    props: {
      opts: { type: Object, required: true },
      isPending: { type: Boolean, default: false },
      limitList: []
    },
    data: _ => ({
      i18n,
      assetMap,
      commonEvents,
      form: {
        sourcePaysForDest: false
      }
    }),
    computed: {
      ...mapGetters([
        vuexTypes.accountLimits
      ]),
      senderFee () {
        if (this.form.sourcePaysForDest) {
          return {
            fixed: add(this.opts.sourceFixedFee, this.opts.destinationFixedFee),
            percent: add(this.opts.sourcePercentFee, this.opts.destinationPercentFee)
          }
        }
        return {
          fixed: this.opts.sourceFixedFee,
          percent: this.opts.sourcePercentFee
        }
      },
      recipientFee () {
        if (this.form.sourcePaysForDest) {
          return { fixed: 0, percent: 0 }
        }
        return {
          fixed: this.opts.destinationFixedFee,
          percent: this.opts.destinationPercentFee
        }
      }
    },
    async created () {
      await this.loadLimits()
    },
    methods: {
      ...mapActions({
        loadLimits: vuexTypes.GET_ACCOUNT_LIMITS
      }),
      formatDate,
      currentAssetLimit () {
        return this.accountLimits.find(limit => limit.tokenCode === this.opts.destAsset)
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '~scss/variables';

  .transfer-confirm__subject-field {
    padding-top: 0;
  }

  .withdrawal-cashbox__row,
  .withdrawal-daily-limit {
    color: rgba($grey, .5);
  }

  .withdrawal-fees__label {
    margin-top: 12px;
    color: $col-text-secondary;
  }
</style>



// WEBPACK FOOTER //
// src/vue/app/withdrawal/make/Withdrawal.Confirm.vue